import React, { useMemo, useState } from 'react';
import { MdArrowBack, MdFlightTakeoff } from 'react-icons/md';
import { useProfile } from '../contexts/ProfileContext';
import { searchFlights } from '../services/backendAPI';

// Flight fare finder (plan 160) — a DEV/DEMO tool. Enter an origin, destination,
// and date; see the sample fares ranked cheapest-first with the cheapest cash and
// the cheapest points fare (valued against the persona's reward balance) called
// out, then "Book" to see the confirm summary. This mirrors lib/daily/flights.js
// client-side so the demo needs no extra API. IMPORTANT: the fares below are
// ILLUSTRATIVE SAMPLE DATA — not real availability, taxes, or live quotes.

const BASIS = 'Based on sample fares, not a live quote.';
const CENTS_PER_POINT = 1.5; // travel valuation, mirrors POINT_VALUE_CENTS.travel

const AIRPORTS = {
  yyz: 'YYZ', toronto: 'YYZ', yvr: 'YVR', vancouver: 'YVR',
  yul: 'YUL', montreal: 'YUL', 'montréal': 'YUL', yyc: 'YYC', calgary: 'YYC',
  nrt: 'NRT', tokyo: 'NRT', japan: 'NRT', lhr: 'LHR', london: 'LHR',
  cdg: 'CDG', paris: 'CDG', jfk: 'JFK', 'new york': 'JFK', nyc: 'JFK',
  lax: 'LAX', 'los angeles': 'LAX',
};
function airportCode(s) {
  const k = String(s || '').trim().toLowerCase();
  if (!k) return null;
  if (AIRPORTS[k]) return AIRPORTS[k];
  const up = k.toUpperCase();
  return /^[A-Z]{3}$/.test(up) && Object.values(AIRPORTS).includes(up) ? up : null;
}

// Sample catalog — mirrors the cheapest carriers in lib/daily/flights.js.
const FARE_CATALOG = {
  'YYZ→YVR': { seasonal: { 6: 1.15, 7: 1.2, 8: 1.15, 12: 1.25 }, carriers: [
    { carrier: 'Air Canada', cashFare: 420, pointsFare: { miles: 25000, cash: 50 }, cabin: 'economy', stops: 0 },
    { carrier: 'WestJet', cashFare: 480, pointsFare: { miles: 20000, cash: 40 }, cabin: 'economy', stops: 0 },
    { carrier: 'Flair', cashFare: 380, pointsFare: { miles: 30000, cash: 35 }, cabin: 'economy', stops: 1 },
  ] },
  'YYZ→NRT': { seasonal: { 3: 1.1, 4: 1.15, 10: 1.05, 12: 1.2 }, carriers: [
    { carrier: 'Air Canada', cashFare: 1450, pointsFare: { miles: 105000, cash: 145 }, cabin: 'economy', stops: 0 },
    { carrier: 'ANA', cashFare: 1520, pointsFare: { miles: 100000, cash: 150 }, cabin: 'economy', stops: 1 },
    { carrier: 'Japan Airlines', cashFare: 1610, pointsFare: { miles: 110000, cash: 160 }, cabin: 'economy', stops: 1 },
  ] },
  'YYZ→LHR': { seasonal: { 6: 1.2, 7: 1.25, 8: 1.2, 12: 1.3 }, carriers: [
    { carrier: 'Air Canada', cashFare: 890, pointsFare: { miles: 60000, cash: 90 }, cabin: 'economy', stops: 0 },
    { carrier: 'British Airways', cashFare: 940, pointsFare: { miles: 55000, cash: 110 }, cabin: 'economy', stops: 0 },
  ] },
  'YYZ→YUL': { seasonal: { 12: 1.15 }, carriers: [
    { carrier: 'Air Canada', cashFare: 210, pointsFare: { miles: 12000, cash: 25 }, cabin: 'economy', stops: 0 },
    { carrier: 'Porter', cashFare: 190, pointsFare: { miles: 14000, cash: 20 }, cabin: 'economy', stops: 0 },
  ] },
  'YYZ→JFK': { seasonal: { 12: 1.2 }, carriers: [
    { carrier: 'Air Canada', cashFare: 320, pointsFare: { miles: 18000, cash: 40 }, cabin: 'economy', stops: 0 },
    { carrier: 'Porter', cashFare: 300, pointsFare: { miles: 20000, cash: 35 }, cabin: 'economy', stops: 0 },
  ] },
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

function pointsBalance(profile) {
  const p = profile?.profile || profile;
  const c = [
    p?.rewards?.points, p?.rewards?.balance, p?.rewardsPoints, p?.pointsBalance, p?.points,
    p?.accounts?.credit_card?.rewards_balance, p?.accounts?.credit_card?.points_balance,
  ];
  for (const v of c) { const n = Number(v); if (Number.isFinite(n) && n > 0) return n; }
  return null;
}

function search({ origin, dest, departDate }, profile) {
  const o = airportCode(origin);
  const d = airportCode(dest);
  const route = o && d ? FARE_CATALOG[`${o}→${d}`] : null;
  const query = { origin: o, dest: d, departDate };
  if (!route) return { query, options: [], cheapestCash: null, cheapestEffective: null };
  const month = departDate ? new Date(departDate).getUTCMonth() + 1 : 0;
  const mult = (route.seasonal && route.seasonal[month]) || 1;
  const balance = pointsBalance(profile);
  let options = route.carriers.map((c) => {
    const cashFare = round2(c.cashFare * mult);
    const miles = c.pointsFare.miles;
    const surcharge = round2(c.pointsFare.cash * mult);
    const pointsDollarValue = round2(miles * CENTS_PER_POINT / 100 + surcharge);
    const effectiveCost = round2(Math.min(cashFare, pointsDollarValue));
    const payWith = pointsDollarValue + 0.01 < cashFare ? 'points' : 'cash';
    const canAffordPoints = balance != null && balance >= miles;
    return { carrier: c.carrier, cabin: c.cabin, stops: c.stops, cashFare,
      pointsFare: { miles, cash: surcharge }, pointsDollarValue, effectiveCost, payWith, canAffordPoints };
  });
  options.sort((a, b) => a.effectiveCost - b.effectiveCost || a.cashFare - b.cashFare);
  const cheapestCash = options.reduce((b, x) => (!b || x.cashFare < b.cashFare ? x : b), null);
  const cheapestEffective = options[0];
  return { query, options, cheapestCash, cheapestEffective };
}

const FlightSearchScreen = ({ onNavigate, onBack }) => {
  const { loadedProfileData } = useProfile();
  const homeCity = loadedProfileData?.profile?.customer?.address?.city
    || loadedProfileData?.customer?.address?.city || 'Toronto';
  const [origin, setOrigin] = useState(homeCity);
  const [dest, setDest] = useState('Tokyo');
  const [departDate, setDepartDate] = useState('2026-10-15');
  const [result, setResult] = useState(null);
  const [booked, setBooked] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMock, setIsMock] = useState(false);

  const balance = useMemo(() => pointsBalance(loadedProfileData), [loadedProfileData]);

  const run = async () => {
    setBooked(null);
    setLoading(true);
    setIsMock(false);
    const fromCode = airportCode(origin) || origin.toUpperCase();
    const toCode   = airportCode(dest)   || dest.toUpperCase();
    try {
      const data = await searchFlights({ from: fromCode, to: toCode, date: departDate });
      if (!data.mock && data.flights && data.flights.length > 0) {
        // Normalize SerpAPI flight objects for display
        const normalized = data.flights.slice(0, 6).map(f => {
          const leg = Array.isArray(f.flights) ? f.flights[0] : null;
          const carrier = leg?.airline || f.airline || 'Unknown';
          const cashFare = f.price || 0;
          const stops = Array.isArray(f.flights) ? Math.max(0, f.flights.length - 1) : 0;
          const cabin = leg?.travel_class || 'Economy';
          return {
            carrier, cashFare, stops, cabin,
            pointsFare: { miles: Math.round(cashFare * 70), cash: Math.round(cashFare * 0.1) },
            pointsDollarValue: round2(Math.round(cashFare * 70) * CENTS_PER_POINT / 100 + Math.round(cashFare * 0.1)),
            canAffordPoints: balance != null && balance >= Math.round(cashFare * 70),
            payWith: 'cash',
            _live: true,
          };
        });
        normalized.sort((a, b) => a.cashFare - b.cashFare);
        const cheapestCash = normalized[0];
        setResult({ query: { origin: fromCode, dest: toCode, departDate }, options: normalized, cheapestCash, cheapestEffective: cheapestCash });
        setIsMock(false);
      } else {
        // Fall back to local mock catalog
        setResult(search({ origin, dest, departDate }, loadedProfileData));
        setIsMock(true);
      }
    } catch (_) {
      setResult(search({ origin, dest, departDate }, loadedProfileData));
      setIsMock(true);
    } finally {
      setLoading(false);
    }
  };
  const book = (opt) => {
    const summary = opt.payWith === 'points' && opt.canAffordPoints
      ? `Booked ${opt.carrier} with ${opt.pointsFare.miles.toLocaleString()} points`
      : `Booked ${opt.carrier} for ${money(opt.cashFare)}`;
    setBooked({ ...opt, summary });
  };

  return (
    <div className="screen bh-flow">
      <header className="bh-flow-hero tall">
        <div className="bh-flow-hero-row">
          <div className="bh-flow-hero-left">
            <button className="bh-flow-back" onClick={onBack || (() => onNavigate && onNavigate('services'))} aria-label="Back">
              <MdArrowBack />
            </button>
            <h1 className="bh-flow-title">Find a Flight</h1>
          </div>
        </div>
        <p className="bh-flow-hero-subtitle" style={{ color: 'var(--bh-black)' }}>
          {isMock ? `Sample fares — ${BASIS}` : 'Live fares via Google Flights'}
        </p>
      </header>

      <div className="bh-flow-body">
        <div className="bh-form-card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2xs)' }}>
          <label style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>From</label>
          <input className="bh-input" value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="Toronto" />
          <label style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>To</label>
          <input className="bh-input" value={dest} onChange={(e) => setDest(e.target.value)} placeholder="Tokyo" />
          <label style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>Depart</label>
          <input className="bh-input" type="date" value={departDate} onChange={(e) => setDepartDate(e.target.value)} />
          <button className="bh-btn-primary" style={{ marginTop: 'var(--space-2xs)' }} onClick={run} disabled={loading}>
            <MdFlightTakeoff style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {loading ? 'Searching…' : 'Search fares'}
          </button>
          {balance != null && (
            <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
              Your balance: {balance.toLocaleString()} points (≈ {money(balance * CENTS_PER_POINT / 100)} toward travel).
            </div>
          )}
        </div>

        {isMock && result && (
          <div className="bh-form-card" style={{ background: 'var(--td-green-light)', borderLeft: '4px solid var(--td-dark-green)' }}>
            <p style={{ margin: 0, font: 'var(--bh-caption)', color: 'var(--td-dark-green)' }}>
              Showing sample fares — live flight data unavailable. {BASIS}
            </p>
          </div>
        )}

        {result && !result.options.length && (
          <div className="bh-form-card">
            <p style={{ margin: 0, color: 'var(--bh-black)' }}>
              No sample fares for that route in the demo catalog. Try Tokyo, Vancouver, London, Montreal, or New York from Toronto.
            </p>
          </div>
        )}

        {result && result.options.map((opt, i) => {
          const isCheapEff = opt === result.cheapestEffective;
          const isCheapCash = opt === result.cheapestCash;
          return (
            <div key={i} className="bh-form-card" style={{
              display: 'flex', flexDirection: 'column', gap: 'var(--space-2xs)',
              outline: isCheapEff ? '2px solid var(--td-green)' : 'none',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, font: 'var(--bh-h3)', color: 'var(--bh-black)' }}>{opt.carrier}</h3>
                <div style={{ display: 'flex', gap: 4 }}>
                  {isCheapCash && <span style={chip('var(--td-green-light)', 'var(--td-dark-green)')}>Cheapest cash</span>}
                  {isCheapEff && opt.payWith === 'points' && <span style={chip('var(--td-green-light)', 'var(--td-dark-green)')}>Best with points</span>}
                </div>
              </div>
              <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
                {opt.stops === 0 ? 'Non-stop' : `${opt.stops} stop`} · {opt.cabin}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-sm)' }}>
                <div>
                  <div style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>Cash</div>
                  <div style={{ font: 'var(--bh-h3)', color: 'var(--bh-black)' }}>{money(opt.cashFare)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>Points</div>
                  <div style={{ font: 'var(--bh-h3)', color: 'var(--bh-black)' }}>
                    {opt.pointsFare.miles.toLocaleString()} pts + {money(opt.pointsFare.cash)}
                  </div>
                  <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>≈ {money(opt.pointsDollarValue)} effective</div>
                </div>
              </div>
              <div style={{ font: 'var(--bh-caption)', color: opt.canAffordPoints ? 'var(--td-dark-green)' : 'var(--bh-gray)' }}>
                {opt.canAffordPoints ? 'You have enough points to cover this.' : 'Not enough points to cover this yet.'}
              </div>
              <button className="bh-btn-primary" onClick={() => book(opt)}>
                Book {opt.payWith === 'points' && opt.canAffordPoints ? 'with points' : `for ${money(opt.cashFare)}`}
              </button>
            </div>
          );
        })}

        {booked && (
          <div className="bh-form-card" style={{ outline: '2px solid var(--td-green)' }}>
            <h3 style={{ margin: 0, font: 'var(--bh-h3)', color: 'var(--td-dark-green)' }}>Confirm booking</h3>
            <p style={{ margin: '6px 0 0', color: 'var(--bh-black)' }}>{booked.summary}</p>
            <p style={{ margin: '6px 0 0', font: 'var(--bh-caption)', color: 'var(--bh-gray)', fontStyle: 'italic' }}>
              Demo only — {BASIS} In the app, IVA books this through a confirm-gated action recorded to your ledger.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

function chip(bg, color) {
  return {
    display: 'inline-flex', alignItems: 'center', background: bg, color,
    font: 'var(--bh-caption)', fontWeight: 700, padding: '3px 9px', borderRadius: 'var(--radius-full)',
  };
}

export default FlightSearchScreen;
